/**
 * WorkDesk — Role-parameterized dashboard surface
 *
 * Per redesign plan §3.1: One component, role-parameterized.
 * Renders metric rail + case DataTable + work queue + Recents rail.
 *
 * Design constraints:
 * - Dark default; Daylight mode via data-theme attribute
 * - Type stack: DM Serif (headings), Outfit (body), DM Mono (numbers/mono)
 * - No literal hex colors — use CSS variable keys
 * - Exactly ONE metric card gets orange left-accent (needs-action metric)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { formatRelativeTime, humanizeLabel } from '@/utils/formatters';
import { useRecents } from '@/hooks/useRecents';
import { DataTable } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ToolTile } from '@/components/ui/ToolTile';
import HandoffLine from '@/components/handoff/HandoffLine';
import { DocsAwaitingOcrPanel } from './DocsAwaitingOcrPanel';
import WorkDeskUtilityBar from './WorkDeskUtilityBar';
import {
  Briefcase,
  FileText,
  TrendingUp,
  Users,
  DollarSign,
  Pin,
  History,
  Loader2,
} from 'lucide-react';
import type { WorkspaceRole } from '@/config/toolsManifest';
import type { WorkDeskProps, WorkDeskCase, MetricConfig } from './WorkDesk.types';
import { getRoleMetrics, getRoleColumns, getNeedsActionKey } from './WorkDesk.config';

/**
 * MetricCard with brand-safe styling
 * Exactly ONE card per role gets the orange left-accent
 */
interface MetricCardProps {
  metric: MetricConfig;
  onClick?: () => void;
}

function MetricCard({ metric, onClick }: MetricCardProps) {
  const hasAccent = metric.isNeedsAction;

  return (
    <div
      className={`
        rounded-[13px] border p-5
        bg-[var(--color-bg-surface)]
        border-[var(--color-border)]
        transition-all duration-200
        ${hasAccent ? 'border-l-4 border-l-[var(--color-brand)]' : ''}
        ${onClick ? 'cursor-pointer hover:bg-[var(--color-bg-hover)]' : ''}
      `}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={`${metric.label}: ${metric.value}`}
      data-testid="workdesk-metric-card"
    >
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-[var(--color-text-muted)]">
          {metric.label}
        </p>
        {metric.badge && (
          <StatusBadge
            text={metric.badge.text}
            variant={metric.badge.variant === 'success' ? 'complete' : metric.badge.variant === 'warning' ? 'pending' : 'neutral'}
            size="sm"
          />
        )}
      </div>

      <div className="mt-3">
        {metric.isMoney ? (
          <FinancialValue
            value={metric.value}
            size="xl"
            className="text-[var(--color-text-primary)]"
          />
        ) : (
          <p className="font-mono text-2xl font-medium tabular-nums text-[var(--color-text-primary)]">
            {metric.value}
          </p>
        )}
      </div>

      {metric.subtitle && (
        <p className="mt-1 text-xs text-[var(--color-text-dim)]">
          {metric.subtitle}
        </p>
      )}
    </div>
  );
}

/**
 * RecentsRail — Sidebar with pinned and recent items
 */
interface RecentsRailProps {
  recents: ReturnType<typeof useRecents>['recents'];
  pinned: ReturnType<typeof useRecents>['pinned'];
  onPin: (item: ReturnType<typeof useRecents>['recents'][0]) => void;
  onUnpin: (id: string) => void;
  isLoading: boolean;
}

function RecentsRail({ recents, pinned, onPin, onUnpin, isLoading }: RecentsRailProps) {
  const navigate = useNavigate();

  const handleItemClick = (href?: string) => {
    if (href) {
      navigate(href);
    }
  };

  if (isLoading) {
    return (
      <div className="w-72 flex-shrink-0 space-y-6">
        <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="flex items-center gap-2 mb-4">
            <Loader2 className="w-4 h-4 animate-spin text-[var(--color-brand)]" />
            <span className="text-sm text-[var(--color-text-muted)]">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 flex-shrink-0 space-y-6">
      {/* Pinned Section */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Pin className="w-4 h-4 text-[var(--color-brand)]" />
          <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
            Pinned
          </h3>
        </div>

        {pinned.length === 0 ? (
          <p className="text-sm text-[var(--color-text-dim)]">
            No pinned items
          </p>
        ) : (
          <div className="space-y-2">
            {pinned.map((item) => (
              <div
                key={item.id}
                className="group flex items-center justify-between p-2 rounded-lg hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
                onClick={() => handleItemClick(item.href)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Pin className="w-3 h-3 text-[var(--color-brand)] flex-shrink-0" />
                  <span className="text-sm text-[var(--color-text-secondary)] truncate">
                    {item.label}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin(item.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)] transition-opacity"
                  aria-label="Unpin"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recents Section */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-[var(--color-text-muted)]" />
          <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
            Recents
          </h3>
        </div>

        {recents.length === 0 ? (
          <p className="text-sm text-[var(--color-text-dim)]">
            No recent activity
          </p>
        ) : (
          <div className="space-y-1">
            {recents.slice(0, 8).map((item) => (
              <div
                key={item.id}
                className="group flex items-center justify-between p-2 rounded-lg hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
                onClick={() => handleItemClick(item.href)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm text-[var(--color-text-secondary)] truncate">
                    {item.label}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPin(item);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-[var(--color-text-dim)] hover:text-[var(--color-brand)] transition-opacity flex-shrink-0"
                  aria-label="Pin"
                >
                  <Pin className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main WorkDesk Component
 */
export function WorkDesk({ role, className }: WorkDeskProps) {
  const navigate = useNavigate();
  const { recents, pinned, pin, unpin, isLoaded, hydrateFromApi, isHydrating } = useRecents();
  const [cases, setCases] = useState<WorkDeskCase[]>([]);
  const [isLoadingCases, setIsLoadingCases] = useState(true);
  const [metrics, setMetrics] = useState<MetricConfig[]>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  // Data-specialist only: 'queue' = my stage queue, 'all' = every org case.
  const [scope, setScope] = useState<'queue' | 'all'>('queue');

  // Hydrate recents from API on mount
  useEffect(() => {
    if (isLoaded) {
      hydrateFromApi();
    }
  }, [isLoaded, hydrateFromApi]);

  // Fetch role-specific data
  const fetchData = useCallback(async () => {
    setIsLoadingCases(true);
    setIsLoadingMetrics(true);

    try {
      // Cases, live queue stats (P0.1 seam — DataSpecialistDashboard.tsx:76),
      // and the pending-issues count all fetch in parallel. Each helper below
      // swallows its own errors and returns a safe default, so one failing
      // call degrades gracefully instead of blanking the whole rail.
      const [casesData, statsData, issuesCount] = await Promise.all([
        fetchCasesForRole(role, scope),
        fetchStatsForRole(role),
        fetchIssuesPendingCount(),
      ]);
      setCases(casesData);

      // Generate metrics based on live stats (config-declared statsKey) or,
      // for roles without one, the existing case-derived fallback.
      const metricsData = generateMetricsForRole(role, casesData, statsData, issuesCount);
      setMetrics(metricsData);
    } catch (e) {
      console.warn('Failed to fetch WorkDesk data:', e);
      // Fallback to empty state
      setCases([]);
      setMetrics(getRoleMetrics(role));
    } finally {
      setIsLoadingCases(false);
      setIsLoadingMetrics(false);
    }
  }, [role, scope]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = getRoleColumns(role);
  const needsActionKey = getNeedsActionKey(role);

  // Update metrics with isNeedsAction flag
  const metricsWithAccent = metrics.map((m) => ({
    ...m,
    isNeedsAction: m.key === needsActionKey,
  }));

  const handleCaseClick = (caseItem: WorkDeskCase) => {
    // The Data Specialist opens each case in the DS Case Workspace shell
    // (CaseWorkspaceShell role="data_specialist", the Wizard-V2-acquired stages:
    // Documents · Client · Event · Transactions · Payroll · Handoff), NOT the
    // standalone /wizard-v2 or /report that the backend primary_action_url points
    // at. Without this the DS never reached their own shell (PRs #1192/#1193).
    if (role === 'data_specialist') {
      // Open on Documents — the first stage (everything derives from it).
      navigate(`/data-specialist/case/${caseItem.id}/documents`);
      return;
    }
    // Other roles: land on the backend-supplied work surface, falling back to the
    // status page only when the backend sent no primary_action_url.
    navigate(caseItem.primaryActionUrl || `/case/${caseItem.id}`);
  };

  return (
    <div className={`min-h-screen bg-[var(--color-bg-base)] ${className || ''}`}>
      <WorkDeskUtilityBar
        roleLabel={`${role.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} WorkDesk`}
        onRefresh={fetchData}
        isRefreshing={isLoadingCases || isLoadingMetrics}
        roleTag={role.replace('_', ' ').toUpperCase()}
      />
      {/* Header */}
      <div className="px-6 py-5 border-b border-[var(--color-hairline)]">
        <div className="max-w-[1320px] mx-auto">
          <h1 className="font-serif text-2xl text-[var(--color-text-primary)]">
            Work Desk
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {role.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} View
          </p>
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-[1320px] mx-auto">
          <div className="flex gap-6">
            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-6">
              {/* Metric Rail */}
              <section aria-label="Metrics">
                {isLoadingMetrics ? (
                  <div className="grid grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5 h-28 animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4">
                    {metricsWithAccent.map((metric) => (
                      <MetricCard
                        key={metric.key}
                        metric={metric}
                        onClick={metric.isNeedsAction ? () => navigate('/queue') : undefined}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Docs awaiting OCR — Data Specialist only */}
              {role === 'data_specialist' && (
                <section aria-label="Docs awaiting OCR">
                  <DocsAwaitingOcrPanel onChanged={fetchData} />
                </section>
              )}

              {/* Cases DataTable */}
              <section aria-label="Cases">
                <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[var(--color-brand)]" />
                      <h2 className="font-serif text-lg text-[var(--color-text-primary)]">
                        Active Cases
                      </h2>
                    </div>
                    {role === 'data_specialist' && (
                      <div
                        role="group"
                        aria-label="Case scope"
                        className="inline-flex items-center rounded-lg border border-border bg-surface p-0.5"
                      >
                        {([
                          { id: 'queue', label: 'My Queue' },
                          { id: 'all', label: 'All Cases' },
                        ] as const).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            aria-pressed={scope === opt.id}
                            onClick={() => {
                              if (scope !== opt.id) setScope(opt.id);
                            }}
                            className={`px-3 py-1 text-[13px] font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand ${
                              scope === opt.id
                                ? 'bg-brand text-white'
                                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {role === 'data_specialist' && scope === 'all' && (
                    <p className="-mt-2 mb-4 text-xs text-[var(--color-text-muted)]">
                      Showing all company cases — every stage
                    </p>
                  )}

                  {isLoadingCases ? (
                    <div className="h-48 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[var(--color-brand)]" />
                    </div>
                  ) : cases.length === 0 ? (
                    <EmptyState
                      icon={<Briefcase className="w-12 h-12" />}
                      title="No active cases"
                      description="Cases will appear here when they're assigned to you."
                    />
                  ) : (
                    <DataTable
                      columns={columns.map((col) => ({
                        ...col,
                        render: role === 'data_specialist' && col.key === 'clientName'
                          ? (value: string, row: WorkDeskCase) => (
                            <div className="min-w-0">
                              <div className="truncate">{value}</div>
                              <HandoffLine handoff={row.handoff} viewerSeat="data_specialist" compact />
                            </div>
                          )
                          : col.key === 'value'
                          ? (value: number) => <FinancialValue value={value} size="md" />
                          : col.key === 'status'
                            ? (value: string, row: WorkDeskCase) => (
                              <StatusBadge
                                text={humanizeLabel(value)}
                                variant={row.needsAttention ? 'warning' : 'complete'}
                                size="sm"
                              />
                            )
                            // P0.3: relative, human-scannable timestamp — DM
                            // Mono, full ISO preserved in the title attr.
                            : col.key === 'lastUpdated'
                              ? (value: string) => (
                                <span
                                  className="font-mono text-xs text-[var(--color-text-muted)]"
                                  title={value}
                                >
                                  {formatRelativeTime(value)}
                                </span>
                              )
                              // P0.4: the backend's next-action sentence —
                              // replaces the dead `needsAttention` column.
                              : col.key === 'nextAction'
                                ? (value: string | undefined) => (
                                  <span
                                    className="block max-w-[280px] truncate text-sm text-[var(--color-text-secondary)]"
                                    title={value || undefined}
                                  >
                                    {value || '—'}
                                  </span>
                                )
                                // P0.4: real <button> deep-linking to the
                                // backend's primary_action_url — the
                                // one-orange-accent rule holds because this
                                // is an OUTLINE button, not a solid fill.
                                : col.key === 'primaryAction'
                                  ? (value: string | undefined, row: WorkDeskCase) => {
                                    if (!value) return null;
                                    // DS opens the DS Case Workspace shell (see handleCaseClick);
                                    // other roles follow the backend-supplied action URL.
                                    const targetUrl = role === 'data_specialist'
                                      ? `/data-specialist/case/${row.id}/documents`
                                      : (row.primaryActionUrl || `/case/${row.id}`);
                                    return (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(targetUrl);
                                        }}
                                        className="rounded-[6px] border border-[var(--color-brand)] px-3 py-1.5 text-xs font-medium text-[var(--color-brand)] transition-colors hover:bg-[var(--color-needs-human-tint)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
                                      >
                                        {value}
                                      </button>
                                    );
                                  }
                                  // FL-5 5.3: compact, neutral (non-orange) icon deep-link
                                  // to the DS per-case extraction review — orange stays
                                  // reserved for the one primary action per row, and the
                                  // icon never clips the narrow column.
                                  : col.key === 'extractionReview'
                                  ? (_value: unknown, row: WorkDeskCase) => (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/data-specialist/case/${row.id}`);
                                      }}
                                      className="inline-flex items-center justify-center rounded-[6px] border border-[var(--color-border)] p-1.5 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
                                      title="Review document extraction & certify"
                                      aria-label="Review document extraction and certify"
                                    >
                                      <FileText className="w-4 h-4" />
                                    </button>
                                  )
                                  : undefined,
                      }))}
                      data={cases}
                      onRowClick={handleCaseClick}
                      emptyMessage="No cases found"
                      className="w-full table-fixed"
                    />
                  )}
                </div>
              </section>
            </div>

            {/* Recents/Pinned Rail */}
            <RecentsRail
              recents={recents}
              pinned={pinned}
              onPin={pin}
              onUnpin={unpin}
              isLoading={isHydrating}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Fetch cases for a specific role
 */
async function fetchCasesForRole(
  role: WorkspaceRole,
  scope: 'queue' | 'all' = 'queue',
): Promise<WorkDeskCase[]> {
  try {
    // Different endpoints based on role
    const endpointMap: Record<WorkspaceRole, string> = {
      admin: '/api/v1/queue/stats',
      analyst: '/api/v1/queue/stats',
      data_specialist: '/api/v1/queue/data-specialist',
      accountant_internal: '/api/v1/drafts?status=active',
      accountant_external: '/api/v1/accountant/reports',
      attorney: '/api/v1/attorney/cases',
      attorney_internal: '/api/v1/attorney/cases',
      client: '/api/v1/portal/cases',
      mediator: '/api/v1/mediations',  // Phase 7 neutral never loads WorkDesk; key required for type completeness
    };

    const endpoint = role === 'data_specialist'
      ? `/api/v1/queue/data-specialist?scope=${scope}`
      : endpointMap[role] || '/api/v1/queue/stats';
    const response = await api.get(endpoint, {
      showErrorToast: false,
    });

    // Transform response to WorkDeskCase format
    // This is a simplified transformation — real implementation would
    // handle each role's specific response shape
    const cases: WorkDeskCase[] = [];

    if (response && Array.isArray(response.cases)) {
      // Field names per CaseSummaryResponse (backend/app/api/v1/queue_api.py:91).
      return response.cases.map((c: any) => ({
        id: c.id || c.report_id || c._id,
        clientName: c.business_name || c.client_name || 'Unknown',
        lawFirm: c.law_firm || c.law_firm_name,
        status: c.workflow_status || c.status || 'unknown',
        value: c.estimated_value || c.claim_amount,
        daysStale: c.days_stale || 0,
        needsAttention: c.transactions_need_review > 0 || c.days_stale >= 5,
        assignedTo: c.assigned_to || c.assigned_queue,
        lastUpdated: c.last_updated || c.updated_at || new Date().toISOString(),
        // P0.4 — previously dropped fields (queue_api.py:112-118).
        primaryAction: c.primary_action,
        primaryActionUrl: c.primary_action_url,
        nextAction: c.next_action,
        readinessIssues: c.readiness_issues || [],
        handoff: c.handoff ?? null,
      }));
    }

    return cases;
  } catch (e) {
    console.warn(`Failed to fetch cases for role ${role}:`, e);
    return [];
  }
}

/**
 * Fetch live queue stats for a role (QueueStatsResponse,
 * backend/app/api/v1/queue_api.py:63) — the same seam
 * DataSpecialistDashboard.tsx:76 already uses. Never throws: a failed
 * fetch resolves to null so metric generation can degrade gracefully.
 */
async function fetchStatsForRole(role: WorkspaceRole): Promise<Record<string, number> | null> {
  try {
    const response = await api.get<Record<string, number>>(
      `/api/v1/queue/stats?user_role=${role}`,
      { showErrorToast: false },
    );
    return response && typeof response === 'object' ? response : null;
  } catch (e) {
    console.warn(`Failed to fetch queue stats for role ${role}:`, e);
    return null;
  }
}

/**
 * Fetch the count of pending data-quality issues (P0.1 `data_issues` card —
 * no QueueStatsResponse field exists for this, so it is sourced from the
 * global issue tracker instead, same seam as UnifiedWorkQueue.tsx:317).
 * Never throws: defaults to 0 on failure.
 */
async function fetchIssuesPendingCount(): Promise<number> {
  try {
    const response = await api.get<{ issues?: unknown[]; total?: number }>(
      '/api/v1/issues/pending',
      { showErrorToast: false },
    );
    if (response && typeof response.total === 'number') return response.total;
    if (response && Array.isArray(response.issues)) return response.issues.length;
    return 0;
  } catch (e) {
    console.warn('Failed to fetch pending issues count:', e);
    return 0;
  }
}

/**
 * Generate metrics for a role based on live stats (where a metric declares
 * a `statsKey`) and case data (fallback, unchanged behavior for roles that
 * don't wire a statsKey yet).
 */
function generateMetricsForRole(
  role: WorkspaceRole,
  cases: WorkDeskCase[],
  stats: Record<string, number> | null,
  issuesPendingCount: number,
): MetricConfig[] {
  const baseMetrics = getRoleMetrics(role);

  // Calculate actual values from cases (legacy fallback path — unchanged).
  const activeCases = cases.length;
  const needsAttention = cases.filter((c) => c.needsAttention).length;
  const totalValue = cases.reduce((sum, c) => sum + (c.value || 0), 0);
  const staleCases = cases.filter((c) => c.daysStale >= 7).length;

  // Update base metrics with real values where applicable
  return baseMetrics.map((m) => {
    // P0.1: config-declared stats seam — real QueueStatsResponse field,
    // never a fake/derived number. A failed stats fetch (stats === null)
    // degrades to 0, a real verified value, not a synthetic "—".
    if (m.statsKey) {
      const raw = stats ? (stats as Record<string, number | null>)[m.statsKey] : undefined;
      // Percent metrics (auto_classify_rate): null/absent → em-dash — NEVER a fabricated 0%
      // (the backend returns null when no classified transactions exist). Otherwise 1dp + "%".
      if (m.isPercent) {
        return { ...m, value: raw == null ? '—' : `${Number(raw).toFixed(1)}%` };
      }
      // Count metrics: a missing/failed stat degrades to 0, a real value, not a synthetic "—".
      return { ...m, value: raw ?? 0 };
    }
    // `data_issues` has no QueueStatsResponse field — sourced separately
    // from /api/v1/issues/pending (see fetchIssuesPendingCount above).
    if (m.key === 'data_issues') {
      return { ...m, value: issuesPendingCount };
    }
    switch (m.key) {
      case 'active_cases':
        return { ...m, value: activeCases };
      case 'needs_attention':
        return { ...m, value: needsAttention };
      case 'total_value':
        return { ...m, value: totalValue };
      case 'stale_cases':
        return { ...m, value: staleCases };
      default:
        return m;
    }
  });
}

export default WorkDesk;
